import { type PropsWithChildren, type ReactNode, useContext } from "react";

import { tenantConfig } from "@tenant-config";
import { useAuthCompanyInfo } from "@vega-api/custom/api/hooks/useAuthCompanyInfo";
import { getFullName, getInitials } from "@vega-api/custom/utils/user";
import { useUserProfile } from "@vega-api/extended/auth/hooks";
import { DefaultErrorPanel } from "@vega-core/components/Cards/DefaultErrorPanel/DefaultErrorPanel";
import { Footer } from "@vega-core/components/Layout/Footer/Footer";
import { LayoutContext } from "@vega-core/components/Layout/LayoutContext";
import { PageContainer } from "@vega-core/components/Layout/PageContainer";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { Navbar } from "@vega-core/components/Navigation/Navbar/Navbar";
import { useSidebarCollapse } from "@vega-core/components/Navigation/Sidebar/hooks/useSidebarCollapse";
import { useSidebarWidth } from "@vega-core/components/Navigation/Sidebar/hooks/useSidebarWidth";
import { Sidebar } from "@vega-core/components/Navigation/Sidebar/Sidebar";
import { SubNavigation } from "@vega-core/components/Navigation/Sidebar/SubNavigation";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { OrganisationNode } from "../SelectOrganisation/OrganisationNode";

type Props = {
  additionalDisclaimer?: string;
  containerStyle?: string;
  pageContainerClassName?: string;
  isLoading?: boolean;
  isError?: boolean;
  horizontalSubnav?: boolean;
  showNavbar?: boolean;
  showOnlySubNav?: boolean;
  hideSubNavigation?: boolean;
  disableOverflow?: boolean;
  orgNode?: ReactNode;
  pageContainerVariant?: "gp" | "lp";
  removePageContainerPadding?: boolean;
  hideFooter?: boolean;
  pinHeight?: boolean;
};

export const LPPageContainerWithNavigation = (
  props: PropsWithChildren<Props>,
) => (
  <PageContainerWithNavigation
    orgNode={<OrganisationNode />}
    pageContainerVariant="lp"
    showNavbar={tenantConfig.features.lp_show_navbar}
    {...props}
  />
);

export function PageContainerWithNavigation({
  additionalDisclaimer,
  children,
  containerStyle,
  pageContainerClassName,
  isError = false,
  horizontalSubnav = false,
  showNavbar,
  showOnlySubNav,
  hideSubNavigation,
  isLoading,
  disableOverflow,
  orgNode,
  pageContainerVariant = "gp",
  removePageContainerPadding,
  hideFooter = false,
  pinHeight = false,
}: PropsWithChildren<Props>) {
  const {
    navigationItems,
    secondaryMenuItems,
    navbarItems,
    navbarUserMenuItems,
    navBarCustomNode,
    onNavigate,
  } = useContext(LayoutContext);
  const {
    logoDark: companyLogoDark,
    logoLight: companyLogoLight,
    name: companyName,
    smallLogoDark: companyLogoSmallDark,
    smallLogoLight: companyLogoSmallLight,
  } = useAuthCompanyInfo();
  const { isCollapsed } = useSidebarCollapse();
  const { screenWidthMinusSidebar } = useSidebarWidth(
    isCollapsed,
    showOnlySubNav,
  );
  const { data: user } = useUserProfile();
  const fullName = user ? getFullName(user) : "";
  const initials = user ? getInitials(user) : "";

  return (
    <div className="flex min-h-dvh defaultTransition">
      <div className="hidden md:block">
        <Sidebar
          companyName={companyName}
          fullName={fullName}
          hideSubNavigation={horizontalSubnav || hideSubNavigation}
          homePath="/"
          initials={initials}
          logoDark={companyLogoDark}
          logoLight={companyLogoLight}
          navigationItems={navigationItems}
          onNavigate={onNavigate}
          organisationNode={orgNode}
          secondaryMenuItems={secondaryMenuItems}
          showNavbar={showNavbar}
          showOnlySubNav={showOnlySubNav}
          smallLogoDark={companyLogoSmallDark}
          smallLogoLight={companyLogoSmallLight}
        />
      </div>
      {showNavbar ? (
        <Navbar
          className={cnTwMerge(`md:${screenWidthMinusSidebar} right-0 p-0`)}
          customNode={navBarCustomNode}
          isLoading={!user}
          items={navbarItems}
          user={{ initials, name: fullName }}
          userMenuItems={navbarUserMenuItems}
        />
      ) : null}
      <PageContainer
        className={pageContainerClassName}
        disableOverflow={disableOverflow || horizontalSubnav}
        removePageContainerPadding={removePageContainerPadding}
        variant={pageContainerVariant}
      >
        {horizontalSubnav && !hideSubNavigation ? (
          <SubNavigation horizontal isCollapsed={isCollapsed} />
        ) : null}
        <div
          className={cnTwMerge("flex min-h-[100dvh] flex-col justify-between", {
            "md:mt-[theme(height.navbar)] md:min-h-[calc(100dvh-theme(height.navbar))]":
              showNavbar && !pinHeight,
            "md:mt-[theme(height.navbar)] md:h-[calc(100dvh-theme(height.navbar))] md:min-h-0":
              showNavbar && pinHeight,
            "md:h-screen md:min-h-0": !showNavbar && pinHeight,
          })}
        >
          {isTruthy(isLoading) ? <LoadingSpinner /> : null}
          <div
            className={cnTwMerge("flex-1 defaultTransition", {
              "min-h-0": pinHeight,
            })}
          >
            {isError ? (
              <DefaultErrorPanel
                description={m.common_error_somethingWentWrongDescription()}
                title={m.common_error_somethingWentWrong()}
                variant="info"
              />
            ) : (
              children
            )}
          </div>
          {!hideFooter ? (
            <Footer
              additionalDisclaimer={additionalDisclaimer}
              containerStyle={containerStyle}
              isLoggedIn
              pageContainerVariant={pageContainerVariant}
              removePageContainerPadding={removePageContainerPadding}
            />
          ) : null}
        </div>
      </PageContainer>
    </div>
  );
}
